import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, Loader2, CalendarClock, Check } from 'lucide-react';
import { TOOL_LABELS } from './offers/ProgramCard';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ withCredentials: true });
const fmt = (d) => (d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—');

export const useMembership = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => ax.get(`${API}/api/portal/membership`).then((r) => setData(r.data)).catch(() => setData({})), []);
  useEffect(() => { load(); }, [load]);
  return [data, load];
};

export default function MembershipCard() {
  const [data, load] = useMembership();
  const [busy, setBusy] = useState(false);
  if (!data) return <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--p-faint)' }}><Loader2 size={14} className="animate-spin" /> Loading…</div>;
  const m = data.membership?.status === 'active' ? data.membership : null;
  if (!m) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3" data-testid="settings-membership-none">
        <p className="text-sm" style={{ color: 'var(--p-muted)' }}>No tool membership yet. Plans start at $9/month, cancel anytime.</p>
        <Link to="/portal/programs#memberships" className="text-sm font-semibold text-[#1D4ED8] hover:underline" data-testid="settings-membership-browse">See membership plans</Link>
      </div>
    );
  }
  const cancel = async () => {
    if (!window.confirm('Cancel your membership? You keep access until the end of this billing period.')) return;
    setBusy(true);
    try { await ax.post(`${API}/api/portal/membership/cancel`); toast.success('Membership will end at the close of this billing period'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not cancel'); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="settings-membership">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-semibold"><Crown size={16} className="text-[#F97316]" /> {m.name} <span className="text-sm font-normal" style={{ color: 'var(--p-muted)' }}>${m.price}/month</span></div>
          <div className="flex items-center gap-1.5 text-sm mt-1" style={{ color: 'var(--p-muted)' }} data-testid="settings-membership-billing">
            <CalendarClock size={14} /> {m.cancel_at_period_end ? `Ends on ${fmt(m.next_billing_date)}` : `Next billing date: ${fmt(m.next_billing_date)}`}
          </div>
        </div>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${m.cancel_at_period_end ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'}`}>{m.cancel_at_period_end ? 'Canceling' : 'Active'}</span>
      </div>
      <ul className="grid sm:grid-cols-2 gap-1.5 mt-4">
        {(data.tier_info?.perks || []).concat(data.tier_info?.pick ? (m.tools || []).map((t) => TOOL_LABELS[t]) : []).filter((v, i, a) => v && a.indexOf(v) === i).map((x) => (
          <li key={x} className="flex gap-2 text-sm" style={{ color: 'var(--p-muted)' }}><Check size={14} className="text-emerald-500 mt-0.5 shrink-0" />{x}</li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-3 mt-5">
        <Link to="/portal/programs#memberships" className="text-sm font-semibold text-[#1D4ED8] hover:underline" data-testid="settings-membership-change">Change plan</Link>
        {!m.cancel_at_period_end && (
          <button onClick={cancel} disabled={busy} className="text-sm font-semibold text-red-500 hover:underline disabled:opacity-50" data-testid="settings-membership-cancel">
            {busy ? 'Canceling…' : 'Cancel membership'}
          </button>
        )}
      </div>
    </div>
  );
}
